import React from 'react';
import { motion } from 'framer-motion';
import { Quote, CheckCircle2 } from 'lucide-react';
import CTAPair from '@/components/site/CTAPair';

const overview = [
  {
    label: 'Client',
    body: 'Challenger brand in remote/mobile WiFi services and GPS pet location collars — two distinct DTC product lines, one unified growth objective.',
  },
  {
    label: 'Campaign goals',
    body: 'Drive net-new buyer acquisition at a target CPA of $100 or less, while simultaneously building brand awareness beyond existing social channels.',
  },
  {
    label: 'Why managed service',
    body: 'The client wanted agile direct-access media planning without the bureaucratic layers of a traditional media agency slowing decisions and diluting strategy.',
  },
  {
    label: 'Why Sellrly',
    body: 'Depth of experience married with AI tech helping DTC brands reach performance goals, a result-oriented operating model, and a demonstrated track record of fast human-based execution.',
  },
];

const drivers = [
  { title: 'Proprietary first-party audience data', body: 'Sellrly’s buyer database identified net-new, in-market audiences beyond Meta and Google’s walled gardens.' },
  { title: 'Fully managed execution', body: 'No agency handoffs, no briefing delays. Media planning, buying, and optimization ran through a single point of accountability.' },
  { title: 'Open-web reach', body: 'Programmatic placements across premium publishers gave the brand incremental reach with audiences it had never previously touched.' },
  { title: 'Speed to optimization', body: 'A short learning curve meant the campaign was performing at scale within the first two weeks, not the first two months.' },
  { title: 'Dual-objective campaign architecture', body: 'Performance (CPA) and awareness goals were balanced simultaneously across both product lines, maximizing media efficiency.' },
];

export default function CaseStudies() {
  return (
    <div data-testid="page-case-studies">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#000919] text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(900px 500px at 88% 12%, rgba(138,1,230,0.18), transparent 60%), radial-gradient(700px 400px at 8% 100%, rgba(255,80,129,0.12), transparent 60%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-20 md:pt-32 md:pb-28">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.02em] text-[#FF5081]"
          >
            Case study
          </motion.p>
          <p className="mt-3 text-sm font-semibold uppercase tracking-[0.22em] text-white/55">DTC challenger brand</p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-display text-[44px] sm:text-5xl lg:text-6xl xl:text-[68px] font-semibold tracking-[-0.025em] leading-[1.02] max-w-5xl"
          >
            From a $100 goal to a <span className="text-[#FF5081]">$39.35 eCPA</span>. In two weeks.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-7 max-w-3xl text-base lg:text-lg text-white/65 leading-relaxed"
          >
            How a challenger brand in remote WiFi and pet tech cut its target cost-per-acquisition by more than half —
            without an agency, without the wait.
          </motion.p>

          {/* Stats blocks */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard label="Target CPA ceiling" value="$100" />
            <StatCard label="eCPA achieved" value="$39.35" />
            <StatCard label="Below target" value="61%" tone="accent" />
          </div>

          <div className="mt-12">
            <CTAPair testIdSuffix="case-hero" secondaryTarget="#case-details" />
          </div>
        </div>
      </section>

      {/* Campaign overview */}
      <section id="case-details" className="relative py-24 md:py-32 bg-white text-[#031D44]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.22em] text-[#FF5081]">Campaign overview</p>
              <h2 className="mt-5 font-display text-5xl sm:text-6xl lg:text-[64px] font-semibold tracking-[-0.025em] leading-[1.02]">
                A direct-access media engine.
              </h2>
              <div className="mt-10 rounded-2xl p-8 bg-[#0E103D] text-white relative overflow-hidden">
                <Quote className="w-7 h-7 text-[#FF5081]" />
                <p className="mt-4 font-display text-xl md:text-2xl leading-snug">
                  “Within two weeks, Sellrly reached an eCPA of $39.35 — nearly 61% below our stated goal. The
                  learning curve was practically nonexistent.”
                </p>
                <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-white/50">DTC brand · Growth lead</p>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {overview.map((o, i) => (
                <motion.div
                  key={o.label}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="rounded-2xl p-7 bg-[#F8F7FC] border border-[#031D44]/8"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FF5081]">{o.label}</p>
                  <p className="mt-4 text-[#031D44]/80 leading-relaxed text-[15px]">{o.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What drove the outcome */}
      <section id="case-drivers" className="relative py-24 md:py-32 bg-[#0B0F2A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.22em] text-[#FF5081]">What drove the outcome</p>
              <h2 className="mt-5 font-display text-5xl sm:text-6xl lg:text-[64px] font-semibold tracking-[-0.025em] leading-[1.02]">
                Five inputs. One outcome.
              </h2>
              <p className="mt-6 text-white/60 leading-relaxed text-[15px] lg:text-base">
                We design every Sellrly engagement so that a few crisp inputs do the heavy lifting — and the results
                show up fast.
              </p>
            </div>
            <ul className="lg:col-span-7 space-y-3">
              {drivers.map((d, i) => (
                <motion.li
                  key={d.title}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="flex items-start gap-4 rounded-2xl p-6 md:p-7 bg-white/[0.03] border border-white/10"
                >
                  <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF5081]/10 text-[#FF5081] border border-[#FF5081]/20 flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="font-display text-lg md:text-xl font-semibold tracking-tight">{d.title}</p>
                    <p className="mt-1.5 text-white/60 leading-relaxed text-[15px]">{d.body}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 md:py-32 bg-white text-[#031D44]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.025em] leading-[1.05]">
            Stop choosing between slow agencies and expensive headcount.
          </h2>
          <p className="mt-5 text-lg text-[#031D44]/60">Let’s hit your number.</p>
          <div className="mt-10 flex justify-center">
            <CTAPair align="center" testIdSuffix="case-final" secondaryTarget="/services" tone="light" />
          </div>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, tone = 'muted' }) {
  const cls =
    tone === 'accent'
      ? 'rounded-2xl p-8 bg-[#FF5081] text-white border border-[#FF5081]'
      : 'rounded-2xl p-8 bg-white/[0.03] border border-white/10 text-white';
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cls}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] opacity-75">{label}</p>
      <p className="mt-5 font-display text-5xl md:text-6xl font-semibold tracking-[-0.02em] leading-none tabular-nums">
        {value}
      </p>
    </motion.div>
  );
}
